import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Rating,
  Stack,
  MenuItem,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  Paper,
  InputAdornment,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import TuneIcon from '@mui/icons-material/Tune';
import StarIcon from '@mui/icons-material/Star';

const categories = ['All', 'Dress', 'Mobile', 'Shoes', 'Electronics', 'Books'];
const genders = ['All', 'Male', 'Female', 'Kids'];
const ratings = [0, 1, 2, 3, 4, 5];
const priceRanges = [
  { label: 'All', min: 0, max: Infinity },
  { label: 'Below ₹500', min: 0, max: 499 },
  { label: '₹500 - ₹1000', min: 500, max: 1000 },
  { label: '₹1000 - ₹1500', min: 1000, max: 1500 },
  { label: 'Above ₹1500', min: 1501, max: Infinity },
];

const ProductList = ({ shopName = 'All', category = 'All', subCategory = 'All', resellerId, products: incomingProducts, onRemove, showFilters = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [searchTerm, setSearchTerm] = useState('');
  const [gender, setGender] = useState('All');
  const [minRating, setMinRating] = useState(0);
  const [products, setProducts] = useState([]);
  const [likedProducts, setLikedProducts] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState(priceRanges[0]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [priceSliderValue, setPriceSliderValue] = useState([0, 5000]);

  const navigate = useNavigate();

  useEffect(() => {
    if (incomingProducts) {
      setProducts(incomingProducts);
    } else {
      fetch('https://reviewhub-lc.onrender.com/api/reviews/all')
        .then((res) => res.json())
        .then((data) => setProducts(data))
        .catch((err) => console.error('Error fetching products:', err));
    }

    const username = localStorage.getItem('username');
    if (username) {
      fetch('https://reviewhub-lc.onrender.com/api/auth/get-likes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data.likes)) {
            setLikedProducts(data.likes);
          }
        })
        .catch((err) => console.error('Error fetching liked products:', err));
    }
  }, [incomingProducts]);

  const filtered = products.filter((p) => {
    const nameMatch = p.productName?.toLowerCase().includes(searchTerm.toLowerCase());
    const categoryMatch = category === 'All' || p.category === category;
    const subCategoryMatch = subCategory === 'All' || p.subCategory === subCategory;
    const genderMatch = gender === 'All' || p.gender === gender;
    const ratingMatch = p.rating >= minRating;
    const shopMatch = shopName === 'All' || p.platform === shopName;
    const resellerMatch = resellerId ? p.reviewerId === resellerId : true;
    const priceMatch = p.price >= selectedPriceRange.min && p.price <= selectedPriceRange.max;
    return nameMatch && categoryMatch && subCategoryMatch && genderMatch && ratingMatch && shopMatch && resellerMatch && priceMatch;
  });

  const toggleLike = async (productId) => {
    const username = localStorage.getItem('username');
    if (!username) {
      alert('Please login to like products');
      return;
    }

    const isLiked = likedProducts.includes(productId);
    const updatedLikes = isLiked
      ? likedProducts.filter(id => id !== productId)
      : [...likedProducts, productId];

    setLikedProducts(updatedLikes);
    localStorage.setItem('likedProducts', JSON.stringify(updatedLikes));

    try {
      await fetch(`https://reviewhub-lc.onrender.com/api/auth/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, productId, liked: !isLiked }),
      });
    } catch (err) {
      console.error('Error updating like:', err);
    }
  };

  const handleAddToCart = async (product) => {
    const username = localStorage.getItem('username');
    if (!username) {
      alert('Please login to add to cart');
      return;
    }

    try {
      await fetch('https://reviewhub-lc.onrender.com/api/auth/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, productId: product._id }),
      });
      alert('Product added to cart!');
    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  };

  const handlePriceChange = (event, newValue) => {
    setPriceSliderValue(newValue);
    setSelectedPriceRange({
      label: 'Custom',
      min: newValue[0],
      max: newValue[1]
    });
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3, maxWidth: '1300px', margin: '0 auto' }}>
      {showFilters && (
        <>
          {/* Search Bar with Meesho/Flipkart style */}
          <Paper
            component="form"
            sx={{
              p: isMobile ? '2px 4px' : '2px 8px',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              mb: 2,
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              border: '1px solid #f5f5f6',
              backgroundColor: '#f5f5f6',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <InputAdornment position="start" sx={{ color: 'text.secondary', mr: 1 }}>
              <SearchIcon />
            </InputAdornment>
            <TextField
              fullWidth
              placeholder="Search for products..."
              variant="standard"
              size="small"
              InputProps={{
                disableUnderline: true,
                style: { fontSize: isMobile ? '14px' : '16px' }
              }}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  padding: isMobile ? '8px 0' : '12px 0',
                },
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isMobile && (
              <IconButton
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                sx={{ p: '10px', color: 'primary.main' }}
              >
                <TuneIcon />
              </IconButton>
            )}
          </Paper>

          {/* Mobile Filters Panel */}
          {isMobile && showMobileFilters && (
            <Paper sx={{ p: 2, mb: 2, borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Price Range
                </Typography>
                <Slider
                  value={priceSliderValue}
                  onChange={handlePriceChange}
                  valueLabelDisplay="auto"
                  aria-labelledby="range-slider"
                  min={0}
                  max={5000}
                  step={100}
                  sx={{ color: '#ff3f6c' }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">₹{priceSliderValue[0]}</Typography>
                  <Typography variant="caption">₹{priceSliderValue[1]}</Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Minimum Rating
                </Typography>
                <ToggleButtonGroup
                  value={minRating}
                  exclusive
                  onChange={(e, newRating) => setMinRating(newRating)}
                  aria-label="rating filter"
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  {ratings.map((r) => (
                    r > 0 && (
                      <ToggleButton
                        key={r}
                        value={r}
                        sx={{
                          p: '4px 8px',
                          borderRadius: '16px',
                          border: '1px solid #e0e0e0',
                          '&.Mui-selected': {
                            backgroundColor: '#ff3f6c',
                            color: 'white',
                          },
                        }}
                      >
                        {r}+ <StarIcon sx={{ fontSize: '14px', ml: 0.5 }} />
                      </ToggleButton>
                    )
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Gender
                </Typography>
                <ToggleButtonGroup
                  value={gender}
                  exclusive
                  onChange={(e, newGender) => setGender(newGender)}
                  aria-label="gender filter"
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  {genders.map((g) => (
                    g !== 'All' && (
                      <ToggleButton
                        key={g}
                        value={g}
                        sx={{
                          p: '4px 12px',
                          borderRadius: '16px',
                          border: '1px solid #e0e0e0',
                          textTransform: 'none',
                          '&.Mui-selected': {
                            backgroundColor: '#ff3f6c',
                            color: 'white',
                          },
                        }}
                      >
                        {g}
                      </ToggleButton>
                    )
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Paper>
          )}

          {/* Desktop Filters */}
          {!isMobile && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Price Range
                  </Typography>
                  <Slider
                    value={priceSliderValue}
                    onChange={handlePriceChange}
                    valueLabelDisplay="auto"
                    aria-labelledby="range-slider"
                    min={0}
                    max={5000}
                    step={100}
                    sx={{ color: '#ff3f6c', mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">₹{priceSliderValue[0]}</Typography>
                    <Typography variant="caption">₹{priceSliderValue[1]}</Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Minimum Rating
                  </Typography>
                  <ToggleButtonGroup
                    value={minRating}
                    exclusive
                    onChange={(e, newRating) => setMinRating(newRating)}
                    aria-label="rating filter"
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    {ratings.map((r) => (
                      r > 0 && (
                        <ToggleButton
                          key={r}
                          value={r}
                          sx={{
                            p: '4px 8px',
                            borderRadius: '16px',
                            border: '1px solid #e0e0e0',
                            '&.Mui-selected': {
                              backgroundColor: '#ff3f6c',
                              color: 'white',
                            },
                          }}
                        >
                          {r}+ <StarIcon sx={{ fontSize: '14px', ml: 0.5 }} />
                        </ToggleButton>
                      )
                    ))}
                  </ToggleButtonGroup>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Gender
                  </Typography>
                  <ToggleButtonGroup
                    value={gender}
                    exclusive
                    onChange={(e, newGender) => setGender(newGender)}
                    aria-label="gender filter"
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    {genders.map((g) => (
                      g !== 'All' && (
                        <ToggleButton
                          key={g}
                          value={g}
                          sx={{
                            p: '4px 12px',
                            borderRadius: '16px',
                            border: '1px solid #e0e0e0',
                            textTransform: 'none',
                            '&.Mui-selected': {
                              backgroundColor: '#ff3f6c',
                              color: 'white',
                            },
                          }}
                        >
                          {g}
                        </ToggleButton>
                      )
                    ))}
                  </ToggleButtonGroup>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Quick Filters
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedPriceRange.label}
                    exclusive
                    onChange={(e, newLabel) => {
                      const range = priceRanges.find(r => r.label === newLabel);
                      if (range) {
                        setSelectedPriceRange(range);
                        setPriceSliderValue([range.min, range.max === Infinity ? 5000 : range.max]);
                      }
                    }}
                    aria-label="price filter"
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    {priceRanges.slice(1, 5).map((range) => (
                      <ToggleButton
                        key={range.label}
                        value={range.label}
                        sx={{
                          p: '4px 8px',
                          borderRadius: '16px',
                          border: '1px solid #e0e0e0',
                          fontSize: '12px',
                          '&.Mui-selected': {
                            backgroundColor: '#ff3f6c',
                            color: 'white',
                          },
                        }}
                      >
                        {range.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}

      <Grid container spacing={isMobile ? 1 : 2}>
        {filtered.map((product) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={3}
            key={product._id}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: isMobile ? 158 : 220,
              minWidth: isMobile ? 158 : 220,
              maxWidth: isMobile ? 158 : 220,
            }}
          >
            <Card
              sx={{
                width: isMobile ? 158 : 220,
                maxWidth: isMobile ? 158 : 220,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: isMobile ? 340 : '100%',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                },
              }}
            >
              <Box sx={{ position: 'relative', width: '100%', pt: '100%', overflow: 'hidden' }}>
                <CardMedia
                  component="img"
                  image={`https://reviewhub-lc.onrender.com/${product.productMainImage}`}
                  alt={product.productName}
                  sx={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    backgroundColor: '#fff'
                  }}
                />

                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    color: likedProducts.includes(product._id) ? 'red' : 'rgba(0,0,0,0.5)',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                    p: 0.5,
                  }}
                  onClick={() => toggleLike(product._id)}
                >
                  {likedProducts.includes(product._id)
                    ? <FavoriteIcon fontSize={isMobile ? 'small' : 'medium'} />
                    : <FavoriteBorderIcon fontSize={isMobile ? 'small' : 'medium'} />}
                </IconButton>

                {product.platform && (
                  <Chip
                    label={product.platform}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      bgcolor:
                        product.platform === 'Flipkart' ? '#2874f0' :
                          product.platform === 'Meesho' ? '#9c27b0' :
                            product.platform === 'Myntra' ? '#ff3f6c' :
                              product.platform === 'Amazon' ? '#ff9900' :
                                product.platform === 'Instagram' ? '#e1306c' : 'grey',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                )}

                {product.discount && (
                  <Chip
                    label={`${product.discount}% OFF`}
                    color="error"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: product.platform ? 36 : 4,
                      left: 4,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>

              <CardContent sx={{
                flexGrow: 1,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: isMobile ? 160 : 200,
              }}>
                <Typography
                  variant={isMobile ? 'body2' : 'subtitle2'}
                  fontWeight="bold"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    minHeight: 40,
                    maxWidth: '100%',
                  }}
                >
                  {product.productName}
                </Typography>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    label={`${product.rating}★`}
                    size="small"
                    sx={{
                      bgcolor: '#4caf50',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '10px' : '12px',
                      height: isMobile ? 20 : 24,
                      px: 1,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    ({product.reviewerId || 0})
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant={isMobile ? 'body1' : 'subtitle1'} fontWeight="bold">
                    ₹{product.price}
                  </Typography>
                  
                  {product.originalPrice && (
                    <Typography
                      variant={isMobile ? 'caption' : 'body2'}
                      color="text.secondary"
                      sx={{ textDecoration: 'line-through' }}
                    >
                      ₹{product.originalPrice}
                    </Typography>
                  )}
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                    gap: 0.5,
                    ...(isMobile && { minHeight: 36 }),
                  }}
                >
                  {onRemove ? (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      sx={{
                        flex: 1,
                        minHeight: isMobile ? 36 : undefined,
                        fontSize: isMobile ? '10px' : '12px',
                        textTransform: 'none',
                      }}
                      onClick={() => onRemove(product._id)}
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={!isMobile && <ShoppingCartIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        flex: 1,
                        minHeight: isMobile ? 36 : undefined,
                        fontSize: isMobile ? '10px' : '12px',
                        textTransform: 'none',
                      }}
                      onClick={() => handleAddToCart(product)}
                    >
                      {isMobile ? 'Cart' : 'Add to Cart'}
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      flex: 1,
                      minHeight: isMobile ? 36 : undefined,
                      fontSize: isMobile ? '10px' : '12px',
                      textTransform: 'none',
                      backgroundColor: '#ff3f6c',
                      '&:hover': {
                        backgroundColor: '#e6395f',
                      },
                    }}
                    onClick={() => navigate(`/product/${product._id}`)}
                  >
                    View
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ProductList;